import React, {useState} from "react";
import {IconButton, Tooltip, Typography} from "@mui/material";
import {Add, DeleteOutlined, DragIndicator} from "@mui/icons-material";
import CCButton from "../Common/CCButton";
import css_buttons from "../Common/css/CCButton.module.scss";
import {Blue3, Grey2, Grey3, Grey6, Grey7, Grey17} from "../../theme/styles";
import {CanvasItem, hiddenButRequired, itemsIn, sectionsFor} from "./designerModel";
import {FormDefinition, Selection} from "./types";

/**
 * The form as it is being drawn: sections, and the fields and widgets inside them, in order.
 *
 * A SKETCH, NOT A PREVIEW. It shows what is asked, where, and how wide — not the real control with
 * its real options. A designer needs to see structure; showing a live picker here would mean loading
 * every option list on every keystroke, and would invite somebody to fill the form in on the design
 * screen. The Preview tab draws it for real.
 *
 * DROP TARGETS ARE THE GAPS BETWEEN ITEMS, not the items themselves. Dropping "onto" a field has to
 * mean either before or after it, and asking the pointer to decide from a midpoint is how a drag
 * lands one place off. A gap is unambiguous: it is exactly the position it looks like.
 */

type Props = {
    definition: FormDefinition;
    selection: Selection;
    disabled?: boolean;
    onSelect: (selection: Selection) => void;
    onAddSection: () => void;
    onRemoveSection: (key: string) => void;
    /** Which tab the canvas is showing, and how to change or add one. */
    openTab: string | null;
    onOpenTab: (key: string) => void;
    onAddTab: () => void;
    onRemoveField: (code: string) => void;
    onRemoveDisplay: (index: number) => void;
    /** A move within the canvas. `before` is the item to land in front of, or null for the end. */
    onMove: (moving: CanvasItem, sectionKey: string | null, before: CanvasItem | null) => void;
    /** A drop from the palette. */
    onDropField: (code: string, sectionKey: string | null, before: CanvasItem | null) => void;
    onDropDisplay: (kind: string, sectionKey: string | null, before: CanvasItem | null) => void;
};

const FIELD_DRAG = "application/x-cres-palette-field";
const DISPLAY_DRAG = "application/x-cres-palette-display";
const MOVE_DRAG = "application/x-cres-move";

export default function Canvas(props: Props) {
    const {definition, selection, disabled, onSelect, onAddSection, onRemoveSection,
           openTab, onOpenTab, onAddTab} = props;
    const [over, setOver] = useState<string | null>(null);
    // THE SAME RULE THE RENDERER FOLLOWS, so the canvas is not quietly a different form from the
    // one people fill in: a section outside the tabs is always shown.
    const tabs = definition.tabs ?? [];
    const sections = sectionsFor(definition, openTab);

    // Anything placed with no section still has to be reachable, or a form imported from elsewhere
    // would show an empty canvas while quietly holding fields.
    const loose = itemsIn(definition, null);

    const drop = (e: React.DragEvent, sectionKey: string | null, before: CanvasItem | null) => {
        e.preventDefault();
        e.stopPropagation();
        setOver(null);
        if (disabled) return;

        const fieldCode = e.dataTransfer.getData(FIELD_DRAG);
        if (fieldCode) return props.onDropField(fieldCode, sectionKey, before);

        const displayKind = e.dataTransfer.getData(DISPLAY_DRAG);
        if (displayKind) return props.onDropDisplay(displayKind, sectionKey, before);

        const moving = e.dataTransfer.getData(MOVE_DRAG);
        if (moving) {
            const parsed = JSON.parse(moving);
            const source = itemsIn(definition, parsed.section ?? null)
                .find(i => (i.kind === "field" ? (i as any).placement.code === parsed.code
                                               : (i as any).index === parsed.index));
            if (source) props.onMove(source, sectionKey, before);
        }
    };

    const gap = (sectionKey: string | null, before: CanvasItem | null, id: string) => (
        <div
            key={id}
            onDragOver={e => { if (!disabled) { e.preventDefault(); e.stopPropagation(); setOver(id); } }}
            onDragLeave={() => setOver(o => (o === id ? null : o))}
            onDrop={e => drop(e, sectionKey, before)}
            style={{
                height: over === id ? 26 : 10,
                borderRadius: 3,
                margin: "1px 0",
                background: over === id ? "rgba(21,101,192,0.12)" : "transparent",
                border: over === id ? `1px dashed ${Blue3}` : "1px dashed transparent",
                transition: "height 90ms",
            }}
        />
    );

    const renderItems = (sectionKey: string | null, items: CanvasItem[]) => (
        <>
            {gap(sectionKey, items[0] ?? null, `${sectionKey}-top`)}
            {items.map((item, index) => (
                <React.Fragment key={item.kind === "field" ? (item as any).placement.code : `d${(item as any).index}`}>
                    <Item item={item} selection={selection} disabled={disabled}
                          sectionKey={sectionKey}
                          onSelect={onSelect}
                          onRemove={() => (item.kind === "field"
                              ? props.onRemoveField((item as any).placement.code)
                              : props.onRemoveDisplay((item as any).index))}/>
                    {gap(sectionKey, items[index + 1] ?? null, `${sectionKey}-${index}`)}
                </React.Fragment>
            ))}
        </>
    );

    return (
        <div style={{flex: 1, minWidth: 0, padding: "0 20px", overflowY: "auto"}}>
            {sections.length === 0 && loose.length === 0 && (
                <div style={{border: `1px dashed ${Grey3}`, borderRadius: 4, padding: 28,
                             textAlign: "center", marginTop: 8}}>
                    <Typography style={{fontSize: 14, color: Grey7, marginBottom: 12}}>
                        A form is made of sections. Add one, then drag questions into it.
                    </Typography>
                    <CCButton className={css_buttons.blueStandardButton} disabled={disabled}
                              onClick={onAddSection}>Add a section</CCButton>
                </div>
            )}

            {/* The tab strip. Absent until somebody adds one, so a form with no tabs looks exactly
                as it always did. */}
            <div style={{display: "flex", alignItems: "center", gap: 20, marginBottom: 14,
                         borderBottom: tabs.length ? `1px solid ${Grey2}` : "none",
                         paddingBottom: tabs.length ? 4 : 0}}>
                {tabs.map(tab => {
                    const active = openTab === tab.key;
                    const selected = selection?.kind === "tab" && selection.key === tab.key;
                    return (
                        <div key={tab.key}
                             onClick={() => { onOpenTab(tab.key); onSelect({kind: "tab", key: tab.key}); }}
                             style={{cursor: "pointer", paddingBottom: 5, fontSize: 14,
                                     fontWeight: active ? 600 : 400,
                                     color: active || selected ? Grey17 : Grey6,
                                     borderBottom: active ? `2px solid ${Blue3}` : "2px solid transparent"}}>
                            {tab.title || tab.key}
                        </div>
                    );
                })}
                <CCButton className={css_buttons.whiteStandardButton} disabled={disabled}
                          onClick={onAddTab}>
                    <Add fontSize="small" style={{marginRight: 6}}/>Tab
                </CCButton>
            </div>

            {loose.length > 0 && (
                <div style={{marginBottom: 18}}>
                    <Typography style={{fontSize: 12, color: Grey6, marginBottom: 6}}>
                        Not in any section
                    </Typography>
                    {renderItems(null, loose)}
                </div>
            )}

            {sections.map(section => {
                const selected = selection?.kind === "section" && selection.key === section.key;
                return (
                    <div key={section.key}
                         onClick={() => onSelect({kind: "section", key: section.key})}
                         style={{border: `1px solid ${selected ? Blue3 : Grey2}`, borderRadius: 4,
                                 padding: "12px 14px 6px", marginBottom: 16, background: "#fff"}}>
                        <div style={{display: "flex", alignItems: "center", gap: 8}}>
                            <Typography style={{fontSize: 15, fontWeight: 600, color: Grey17, flex: 1}}>
                                {section.title || section.key}
                            </Typography>
                            <Tooltip title="Remove this section and everything in it">
                                <span>
                                    <IconButton size="small" disabled={disabled}
                                                onClick={e => { e.stopPropagation(); onRemoveSection(section.key); }}>
                                        <DeleteOutlined fontSize="small"/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </div>
                        {section.description && (
                            <Typography style={{fontSize: 12, color: Grey6, marginBottom: 4}}>
                                {section.description}
                            </Typography>
                        )}
                        {renderItems(section.key, itemsIn(definition, section.key))}
                    </div>
                );
            })}

            {sections.length > 0 && (
                <CCButton className={css_buttons.whiteStandardButton} disabled={disabled}
                          onClick={onAddSection}>
                    <Add fontSize="small" style={{marginRight: 6}}/>Add a section
                </CCButton>
            )}
        </div>
    );
}

function Item({item, selection, disabled, sectionKey, onSelect, onRemove}: {
    item: CanvasItem; selection: Selection; disabled?: boolean; sectionKey: string | null;
    onSelect: (s: Selection) => void; onRemove: () => void;
}) {
    const isField = item.kind === "field";
    const code = isField ? (item as any).placement.code : null;
    const selected = isField
        ? selection?.kind === "field" && selection.code === code
        : selection?.kind === "display" && selection.index === (item as any).index;

    const placement = isField ? (item as any).placement : null;
    const display = !isField ? (item as any).item : null;

    const width = placement?.width ?? "full";
    const widthLabel = width === "full" ? "" : width;

    return (
        <div
            draggable={!disabled}
            onDragStart={e => {
                e.dataTransfer.setData(MOVE_DRAG, JSON.stringify(
                    isField ? {kind: "field", code, section: sectionKey}
                            : {kind: "display", index: (item as any).index, section: sectionKey}));
                e.dataTransfer.effectAllowed = "move";
            }}
            onClick={e => {
                e.stopPropagation();
                onSelect(isField ? {kind: "field", code: code!}
                                 : {kind: "display", index: (item as any).index});
            }}
            style={{
                display: "flex", alignItems: "center", gap: 8,
                border: `1px solid ${selected ? Blue3 : Grey2}`,
                background: selected ? "rgba(21,101,192,0.05)" : "#fff",
                borderRadius: 3, padding: "8px 10px", cursor: disabled ? "default" : "grab",
            }}
        >
            <DragIndicator fontSize="small" style={{color: Grey3}}/>
            <div style={{flex: 1, minWidth: 0}}>
                {isField ? (
                    <>
                        <Typography style={{fontSize: 13, color: Grey17, fontWeight: 500}}>
                            {placement.label || placement.name || placement.code}
                            {placement.new && (
                                <span style={{fontSize: 10, color: "#B26500", marginLeft: 8,
                                              fontWeight: 700, letterSpacing: "0.4px"}}>
                                    NOT ON THE SHAPE
                                </span>
                            )}
                            {/* Visible without selecting the field, so it is not something you have
                                to go looking for. */}
                            {hiddenButRequired(placement) && (
                                <span style={{fontSize: 10, color: "#B3261E", marginLeft: 8,
                                              fontWeight: 700, letterSpacing: "0.4px"}}>
                                    REQUIRED WHILE HIDDEN
                                </span>
                            )}
                        </Typography>
                        <Typography style={{fontSize: 11, color: Grey6}}>
                            {placement.code}{widthLabel ? ` · ${widthLabel}` : ""}
                            {placement.readOnly ? " · read-only" : ""}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography style={{fontSize: 13, color: Grey17, fontWeight: 500,
                                            fontStyle: display.kind === "divider" ? "italic" : "normal"}}>
                            {display.kind === "divider" ? "Divider" : (display.text || `(empty ${display.kind})`)}
                        </Typography>
                        <Typography style={{fontSize: 11, color: Grey6}}>{display.kind}</Typography>
                    </>
                )}
            </div>
            <IconButton size="small" disabled={disabled}
                        onClick={e => { e.stopPropagation(); onRemove(); }}>
                <DeleteOutlined fontSize="small"/>
            </IconButton>
        </div>
    );
}
